<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const event = inject('tiqCurrentEvent', null);
// A visitor never meets the slot's placeholder: with no real event
// to render it draws nothing, and only the editor keeps the stand-in.
const editing = inject('tiqEditorCanvas', false);

// With no event injected this is the editor canvas, which shows the
// slot rather than an empty strip.
const location = computed(() => event?.location || 'Event location');
const isPlaceholder = computed(() => !event?.location);
const align = computed(() => (['left', 'center', 'right'].includes(props.config.align) ? props.config.align : 'left'));
const iconColor = computed(() => props.config.style?.icon || 'var(--accent)');

// The server decides where it goes (EventMapLink): the check-in pin,
// or the place name searched around the org's own trails — a search
// rather than a guessed pin, which would send somebody to the wrong
// trailhead. The bare search is only for a page served before map_url.
const mapsHref = computed(() => (isPlaceholder.value
    ? null
    : (event.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.value)}`)));
</script>

<template>
    <section v-if="event || editing" :id="config.anchor_id || null"
             class="th-block th-sec th-sec--tight th-eloc"
             :class="`is-align-${align}`"
             :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-row th-eloc__inner">
                <span class="th-eloc__icon" :style="{ color: iconColor }" aria-hidden="true">&#9679;</span>
                <span class="th-eloc__text">{{ location }}</span>
                <a v-if="mapsHref" class="th-eloc__map" :href="mapsHref" target="_blank" rel="noopener noreferrer">
                    Open in maps
                </a>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-eloc__inner { max-width: 820px; gap: 10px; font-size: 17px; }
.th-eloc.is-align-center .th-eloc__inner { justify-content: center; margin: 0 auto; }
.th-eloc.is-align-right .th-eloc__inner { justify-content: flex-end; margin-left: auto; }
.th-eloc__icon { font-size: 12px; line-height: 1; }
.th-eloc__text { font-weight: 600; color: var(--text-heading, inherit); }
.th-eloc__map {
    font: 500 12px/1 var(--th-mono);
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--text-sub, var(--th-meta));
    border-bottom: 1px solid var(--accent);
    padding-bottom: 2px;
    text-decoration: none !important;
}
</style>
